import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';

import { webformGet } from '@uncinc/react-drupal-core';
import type { ImmutableRootState, Langcodes } from '@uncinc/react-drupal-core/types';
import ContextualActionsButtons from '@uncinc/react-drupal-core/dist/Components/ContextualActions/Buttons';
import { viewGet } from '@uncinc/react-drupal-core/dist/redux/actions/Entity/View';

import ModalActions from '../../containers/Modal/Actions';
import useProgrammaticFocus from '../../hooks/useProgrammaticFocus';
import { fetchMessages } from '../../store/actions/Messages';
import { DEFAULT_LANGUAGE, SUBMISSION_VIEW } from '../../config/constants';
import WebformComponent from './Component';
import WebformSubmission from './Submission';
import { useModalContext } from '../../containers/Modal/Context';
import { webformSuccesfulSubmit } from '../../store/actions/Webform';

import type {
  Method,
  Submission,
  IMap,
  WebformProps,
} from './types';

const Webform: React.FC<WebformProps> = ({
  id,
  onClose,
  ...props
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const language = useSelector<ImmutableRootState<Langcodes>, Langcodes>((state) => state.getIn(['ui', 'language'], DEFAULT_LANGUAGE));
  const [method, setMethod] = useState<Method>('PATCH');
  const [hasSubmitted, setSubmitted] = useState(false);

  const webform = useSelector<ImmutableRootState<IMap>, IMap>((state) => state.getIn(['webform', id], Immutable.Map()));
  const webformLangcode = webform.getIn(['info', 'langcode']);
  const submission = useSelector<ImmutableRootState<Submission>, Submission>((state) => state.getIn(['exerciseSubmissions', 'submissions', id]));
  const confirmationMessage = useSelector<ImmutableRootState<string>, string>((state) => (
    state.getIn(['webform', id, 'info', 'settings', 'confirmation_message'], '')
  ));

  // Submission view disabled for now on Zelfhulp Naasten.
  // The Webforms are too complex and the view needs more work display properly.
  const submissionViewEnabled = useMemo(() => (
    SUBMISSION_VIEW && webform.getIn(['info', 'categories', 0]) === 'list'
  ), [webform]);

  useEffect(() => {
    // Checks if the webform is loaded (in the correct language).
    if (webformLangcode !== language) {
      // @ts-ignore
      dispatch(webformGet(id));
    }
  }, [id, webformLangcode, language, dispatch]);

  useEffect(() => {
    if (!submission) {
      setMethod('POST');
    } else if (submissionViewEnabled) {
      setMethod('GET');
    }
  }, [submission, submissionViewEnabled]);

  const onSubmit = useCallback(() => {
    setSubmitted(false);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    dispatch(webformSuccesfulSubmit(id));

    dispatch(viewGet('exercise_answers', 'default', 0, null, {}));

    // Fetch messages for possible new badges.
    // @ts-ignore
    dispatch(fetchMessages());

    if (submissionViewEnabled) {
      setMethod('GET');
    } else {
      onClose();
    }
  }, [dispatch, id, submissionViewEnabled, onClose]);

  const focusToBody = useProgrammaticFocus(document.body);

  const { modalRef } = useModalContext();

  if (!webform) {
    return null;
  }

  const entities = webform.get('fields', Immutable.Map()) as IMap;
  const info = webform.get('info', Immutable.Map()) as IMap;

  return (
    <ContextualActionsButtons entityType="webform" entity={{ id }}>
      {method === 'GET' ? (
        <WebformSubmission
          id={id}
          entities={entities}
          info={info}
          exerciseSubmission={submission}
          onEdit={() => {
            setMethod('PATCH');
            focusToBody();
          }}
          onClose={onClose}
          {...props}
        />
      ) : (
        <>
          <WebformComponent
            id={id}
            entities={entities}
            info={info}
            method={method}
            onNextMultiFormStep={() => {
              modalRef?.current?.scrollTo(0, 0);
              focusToBody();
            }}
            onPrevMultiFormStep={() => {
              modalRef?.current?.scrollTo(0, 0);
              focusToBody();
            }}
            onSuccessfulSubmit={() => {
              setSubmitted(true);

              if (confirmationMessage) {
                return;
              }

              modalRef?.current?.scrollTo(0, 0);
              focusToBody();

              onSubmit();
            }}
            uuid={submission ? submission.uuid : undefined}
            initialValues={submission ? submission.data : undefined}
            {...props}
          />

          {(confirmationMessage && hasSubmitted) && (
            <ModalActions
              primaryActionTitle={t('webform.actions.submit_label')}
              onPrimaryAction={onSubmit}
            />
          )}
        </>
      )}
    </ContextualActionsButtons>
  );
};

export default Webform;
